import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useModal } from "react-modal-state";
import { UserContext } from "../context/UserContext";
import { ChatbotContext } from "../context/ChatbotContext";
import Icon from "./common/icons";
import { AccountMenu, BrandLogo, PHONE_QUERY, StudentTabBar } from "./pages/StudentNav";
import ClosingBanner from "./common/ClosingBanner";

// The landing page header: the same yellow bar as the student pages, with the page's
// section links. Signed-in students also get the account menu and, on phones, the tab bar.
// The landing tour (StudentTour.js) opens the account menu (tour:openUserMenu) and points at it.
const MENU_ITEMS = [
  { key: "results", label: "Results", icon: "bar-chart", to: "/result", id: "header-results-link" },
  { key: "appointments", label: "Appointments", icon: "calendar", to: "/appointments" },
  { key: "profile", label: "Profile", icon: "user", to: "/account" },
];

// Icons for the landing page's section links in the phone menu
const SECTION_ICONS = {
  "/#home": "home",
  "/#our-assessments": "clipboard-list",
  "/#anna-demonstration": "play-circle",
  "/#feedback": "chat-text",
};

const Header = ({ navlinks, handleLogout }) => {
  const { isAuthenticated, user } = useContext(UserContext);
  const { open: openLoginModal } = useModal("login");
  const { setShowbot } = useContext(ChatbotContext);
  const [menuOpen, setMenuOpen] = useState(false);
  const [linksOpen, setLinksOpen] = useState(false);
  const linksRef = useRef(null);
  const name = user?.full_name || "Student";
  const detail = [user?.organizational_level_name, user?.section_name].filter(Boolean).join(" · ");

  // Log out here too when another tab removes the token
  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === "authToken" && !localStorage.getItem("authToken") && isAuthenticated) {
        handleLogout();
      }
    };
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, [isAuthenticated, handleLogout]);

  // On small screens the chatbot would cover an open menu
  const hideChatbotOnSmallScreens = useCallback(() => {
    if (window.innerWidth <= 991) setShowbot(false);
  }, [setShowbot]);

  const changeMenuOpen = useCallback((open) => {
    setMenuOpen(open);
    if (open) {
      setLinksOpen(false);
      hideChatbotOnSmallScreens();
    }
  }, [hideChatbotOnSmallScreens]);

  // Let the tour open and close the account menu
  useEffect(() => {
    const openMenu = () => changeMenuOpen(true);
    const closeMenu = () => setMenuOpen(false);
    window.addEventListener("tour:openUserMenu", openMenu);
    window.addEventListener("tour:closeUserMenu", closeMenu);
    return () => {
      window.removeEventListener("tour:openUserMenu", openMenu);
      window.removeEventListener("tour:closeUserMenu", closeMenu);
    };
  }, [changeMenuOpen]);

  // Phone section links: close on Esc, a click outside, or growing past phone width
  useEffect(() => {
    if (!linksOpen) return undefined;
    const query = window.matchMedia(PHONE_QUERY);
    const close = () => setLinksOpen(false);
    const onKeyDown = (event) => event.key === "Escape" && close();
    const onPointerDown = (event) => {
      if (!linksRef.current?.contains(event.target)) close();
    };
    const onResize = () => !query.matches && close();
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("mousedown", onPointerDown);
    query.addEventListener("change", onResize);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("mousedown", onPointerDown);
      query.removeEventListener("change", onResize);
    };
  }, [linksOpen]);

  const toggleLinks = () => {
    if (!linksOpen) {
      setMenuOpen(false);
      hideChatbotOnSmallScreens();
    }
    setLinksOpen(!linksOpen);
  };

  // Scroll to a landing page section so it starts below the fixed bar. Closing the phone
  // menu removes the link before the browser could follow it, so this does the scrolling.
  const goToSection = (event, href) => {
    event.preventDefault();
    setLinksOpen(false);
    const target = document.getElementById(href.split("#")[1] || "");
    if (!target) {
      window.location.assign(href);
      return;
    }
    // The landing page scrolls <body>, not the window, so scroll the section into view
    const barHeight = linksRef.current?.offsetHeight || 0;
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    window.history.replaceState(window.history.state, "", href);
    target.style.scrollMarginTop = `${barHeight + 8}px`;
    target.scrollIntoView({ behavior: reduceMotion ? "auto" : "smooth", block: "start" });
  };

  const sectionLinks = navlinks.map((link) => (
    <a key={link.link} href={link.link} className="lh-link" onClick={(event) => goToSection(event, link.link)}>
      {link.text}
    </a>
  ));

  return (
    <>
      <header className="sh lh" ref={linksRef}>
        <div className="container lh-row">
          <BrandLogo />
          <nav className="lh-links" aria-label="Page sections">{sectionLinks}</nav>
          <div className="lh-actions">
            {isAuthenticated ? (
              <AccountMenu
                open={menuOpen}
                onOpenChange={changeMenuOpen}
                name={name}
                detail={detail}
                items={MENU_ITEMS}
                onLogout={handleLogout}
              />
            ) : (
              <button type="button" className="lh-login" onClick={() => openLoginModal()}>
                Log in
              </button>
            )}
            <button
              type="button"
              className="lh-menu-btn"
              aria-expanded={linksOpen}
              aria-controls="lh-links-panel"
              aria-label="Page sections"
              onClick={toggleLinks}
            >
              <Icon name={linksOpen ? "x" : "menu"} size={22} />
            </button>
          </div>
        </div>
        {linksOpen && (
          <>
            <div className="lh-scrim" aria-hidden="true" onClick={() => setLinksOpen(false)} />
            <nav id="lh-links-panel" className="lh-links-panel" aria-labelledby="lh-links-title">
              <div className="lh-links-title" id="lh-links-title">On this page</div>
              {navlinks.map((link) => (
                <a key={link.link} href={link.link} className="lh-link" onClick={(event) => goToSection(event, link.link)}>
                  <span className="lh-link-icon">
                    <Icon name={SECTION_ICONS[link.link] || "chevron-right"} size={18} />
                  </span>
                  {link.text}
                </a>
              ))}
            </nav>
          </>
        )}
      </header>
      <div className="sh-spacer" aria-hidden="true" />
      {/* C4: the closing notice on the home page too (it shows nothing otherwise) */}
      {isAuthenticated && <ClosingBanner />}
      {isAuthenticated && <StudentTabBar active="home" />}
    </>
  );
};

export default Header;
